import type { ReactNode, RefObject } from 'react'
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react'
import cc from 'classcat'

export type StickyHeaderBehavior = 'alwaysVisible' | 'revealOnScrollUp'

type StickyHeaderContextValue = {
  headerRef: RefObject<HTMLElement | null>
  isStickyHeaderShown: boolean
  isStickyHeaderRevealedOnScrollUp: boolean
  stickyHeaderClass: string
  headerCallbackRef: (node: HTMLElement | null) => void
  suppressReveal: () => void
  restoreReveal: () => void
}

const StickyHeaderContext = createContext<StickyHeaderContextValue | undefined>(undefined)

type StickyHeaderProviderProps = {
  readonly children: ReactNode
  readonly enabled: boolean
  readonly behavior: StickyHeaderBehavior
}

export function StickyHeaderProvider({ children, enabled, behavior }: StickyHeaderProviderProps) {
  // isHidden === true if scrolled down enough to hide the header.
  // Will only have an effect for `revealOnScrollUp' header
  // and 'alwaysVisible' behavior header on mobile.
  // For 'alwaysVisible' behavior header on desktop, hiding the header will be
  // prevented via CSS media query.
  const [isHidden, setIsHidden] = useState(false)
  const [headerNode, setHeaderNode] = useState<HTMLElement | null>(null)
  const lastScrollY = useRef(0)
  const scrollDownStartY = useRef<number | null>(null)
  const scrollDownHideThreshold = useRef(0)
  const headerRef = useRef<HTMLElement | null>(null)
  const revealSuppressed = useRef(false)

  const headerCallbackRef = useCallback((node: HTMLElement | null) => {
    headerRef.current = node
    setHeaderNode(node)
  }, [])

  useEffect(() => {
    if (!enabled) {
      setIsHidden(false)
      scrollDownStartY.current = null
      scrollDownHideThreshold.current = 0
      return
    }

    const handleScroll = () => {
      const currentY = window.scrollY

      const scrollingDown = currentY > lastScrollY.current
      const scrollingUp = currentY < lastScrollY.current

      if (scrollingDown) {
        if (scrollDownStartY.current === null) {
          scrollDownStartY.current = lastScrollY.current
          scrollDownHideThreshold.current =
            (headerNode?.getBoundingClientRect().height ?? 0) + getTopBarsHeightAboveHeader(headerNode)
        }

        const scrolledDistance = currentY - scrollDownStartY.current
        setIsHidden(scrolledDistance >= scrollDownHideThreshold.current && currentY > 0)
      } else if (scrollingUp || currentY <= 0) {
        scrollDownStartY.current = null
        scrollDownHideThreshold.current = 0
        // On iOS, elastic overscroll at the page bottom lets scrollY exceed maxScrollY briefly.
        // The snap-back looks like an upward scroll and would incorrectly reveal the header.
        const maxScrollY = Math.max(0, document.documentElement.scrollHeight - window.innerHeight)
        const isBouncingBackFromBottom = maxScrollY > 0 && lastScrollY.current > maxScrollY
        if (!revealSuppressed.current && !isBouncingBackFromBottom) {
          setIsHidden(false)
        }
      }

      lastScrollY.current = currentY
    }

    handleScroll()

    window.addEventListener('scroll', handleScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', handleScroll)
    }
  }, [enabled, headerNode])

  // Track the actual bottom position of the header in real-time
  // so the toolbar can stick to it while scrolling
  useEffect(() => {
    if (!enabled || !headerNode) return

    let rafId: number | null = null
    let transitionRafId: number | null = null
    let isTransitioning = false

    const updateToolbarPosition = () => {
      const headerRect = headerNode.getBoundingClientRect()
      const toolbarTop = Math.max(0, headerRect.bottom)
      document.body.style.setProperty('--toolbar-dynamic-top', `${toolbarTop}px`)
    }

    const handleScrollForToolbar = () => {
      if (rafId) return // Already scheduled
      rafId = requestAnimationFrame(() => {
        updateToolbarPosition()
        rafId = null
      })
    }

    // During the header's CSS transform transition (reveal/hide) no scroll events
    // fire, so the rAF-on-scroll approach misses the 350ms animation window.
    // Run a dedicated rAF loop for the duration of the transition.
    const runTransitionLoop = () => {
      updateToolbarPosition()
      if (isTransitioning) transitionRafId = requestAnimationFrame(runTransitionLoop)
    }

    const onTransitionStart = (e: TransitionEvent) => {
      if (e.propertyName !== 'transform') return
      if (transitionRafId) cancelAnimationFrame(transitionRafId)
      isTransitioning = true
      transitionRafId = requestAnimationFrame(runTransitionLoop)
    }

    const onTransitionEnd = (e: TransitionEvent) => {
      if (e.propertyName !== 'transform') return
      isTransitioning = false
      if (transitionRafId) cancelAnimationFrame(transitionRafId)
      transitionRafId = null
      updateToolbarPosition()
    }

    updateToolbarPosition() // Initial update — synchronous, no rAF delay
    window.addEventListener('scroll', handleScrollForToolbar, { passive: true })
    headerNode.addEventListener('transitionrun', onTransitionStart)
    headerNode.addEventListener('transitionend', onTransitionEnd)
    headerNode.addEventListener('transitioncancel', onTransitionEnd)

    return () => {
      window.removeEventListener('scroll', handleScrollForToolbar)
      headerNode.removeEventListener('transitionrun', onTransitionStart)
      headerNode.removeEventListener('transitionend', onTransitionEnd)
      headerNode.removeEventListener('transitioncancel', onTransitionEnd)
      if (rafId) cancelAnimationFrame(rafId)
      if (transitionRafId) cancelAnimationFrame(transitionRafId)
      document.body.style.removeProperty('--toolbar-dynamic-top')
    }
  }, [enabled, headerNode])

  const isStickyHeaderRevealedOnScrollUp = enabled && behavior === 'revealOnScrollUp' && !isHidden
  // For the 'alwaysVisible' sticky header, isHidden is actually also true when scrolling down far enough,
  // but hiding the 'alwaysVisible' header is a mobile only exception and prevented on desktop via CSS media queries.
  // As isStickyHeaderShown is only used in ToolbarTop and the toolbar is only sticky on desktop,
  // we intentionally disregard the isHidden state for 'alwaysVisible' header behavior.
  const isStickyHeaderShown = (enabled && behavior === 'alwaysVisible') || isStickyHeaderRevealedOnScrollUp
  const stickyHeaderClass = enabled
    ? cc([
        'site-header-sticky',
        behavior === 'alwaysVisible' && 'site-header-sticky-always',
        behavior === 'revealOnScrollUp' && 'site-header-sticky-reveal',
        isHidden && 'site-header-sticky-hidden',
      ])
    : ''

  // Prevents the header from revealing in response to the next programmatic upward scroll(s).
  // Call suppressReveal() before triggering any programmatic scroll, restoreReveal() once done.
  // Used for example in forms via useScrollInvalidElementIntoView, to prevent the header
  // from revealing when auto-scrolling to the first invalid field on submit.
  const suppressReveal = useCallback(() => {
    revealSuppressed.current = true
  }, [])
  const restoreReveal = useCallback(() => {
    revealSuppressed.current = false
  }, [])

  const contextValue = useMemo(
    () => ({
      headerRef,
      isStickyHeaderShown,
      isStickyHeaderRevealedOnScrollUp,
      stickyHeaderClass,
      headerCallbackRef,
      suppressReveal,
      restoreReveal,
    }),
    [
      isStickyHeaderShown,
      isStickyHeaderRevealedOnScrollUp,
      stickyHeaderClass,
      headerCallbackRef,
      suppressReveal,
      restoreReveal,
    ],
  )

  return <StickyHeaderContext.Provider value={contextValue}>{children}</StickyHeaderContext.Provider>
}

export function useStickyHeaderContext(): StickyHeaderContextValue | undefined {
  return useContext(StickyHeaderContext)
}

function getTopBarsHeightAboveHeader(headerNode: HTMLElement | null): number {
  if (!headerNode) return 0

  const container = headerNode.closest('.container')
  if (!container) return 0

  const barElements = Array.from(
    container.querySelectorAll<HTMLElement>(
      '.delivery-country-selection-topbar, .header-announcement-bar, .section-usp, .header-usp.mobile-view, .header-usp.desktop-view',
    ),
  )

  return barElements.reduce((sum, element) => {
    const isNestedUspElement =
      (element.matches('.header-usp.mobile-view') || element.matches('.header-usp.desktop-view')) &&
      Boolean(element.closest('.section-usp'))
    if (isNestedUspElement) return sum

    const computedStyle = globalThis.window?.getComputedStyle(element)
    if (!computedStyle || computedStyle.display === 'none' || computedStyle.visibility === 'hidden') return sum

    return sum + element.getBoundingClientRect().height
  }, 0)
}
